/* eslint-disable camelcase */
import React, { useState } from "react"
import { useLocation } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import {
  Box,
  Paper,
  TextField,
  Grid,
  Typography,
  Divider,
  LinearProgress,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { api, showWarning } from "../../../api"

import AssignmentIndIcon from "@mui/icons-material/AssignmentInd"
import AnalyticsIcon from "@mui/icons-material/Analytics"
import PersonIcon from "@mui/icons-material/Person"
import PrintIcon from "@mui/icons-material/Print"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const getEstadoLegible = (status) => {
  const statusMap = { G: "GENERADO", P: "EN PROCESO", C: "CERRADO", F: "FINALIZADO", X: "ANULADO" }
  return statusMap[status] || status
}

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value || 0)

const BuscarTomaFisica = () => {
  const location = useLocation()
  const tomnumero_state = location.state?.tomnumero || ""

  // Extracción de LocalStorage para el encabezado del reporte
  let authData = {}
  try {
    authData = JSON.parse(localStorage.getItem("jwt") || localStorage.getItem("seleccion") || "{}")
  } catch (e) {
    console.error("Error leyendo authData")
  }

  const ciaNombre = authData?.seleccion?.cliciacianombre || "COMPAÑÍA NO IDENTIFICADA"
  const locDescri = authData?.localidad?.locdescri || "MATRIZ"
  const usuarioLogueado = authData?.user || "SISTEMA"

  const [modalImprimir, setModalImprimir] = useState(false)
  const [tipoReporte, setTipoReporte] = useState("CONTEO")
  const [isPrinting, setIsPrinting] = useState(false)

  if (!tomnumero_state) {
    showWarning("No se especificó un número de tomo para consultar.")
  }

  const { data: tomoData, isLoading } = useQuery({
    queryKey: ["getTomoCabecera", tomnumero_state],
    queryFn: async () => {
      try {
        const response = await api.post("/TomaFisica/getTomoCabecera", { tomnumero: tomnumero_state })
        return response?.data?.data || response?.data || {}
      } catch (error) {
        return {}
      }
    },
    enabled: !!tomnumero_state,
    refetchOnWindowFocus: false,
  })

  const infoTomo = tomoData || {}
  const operarios = Array.isArray(infoTomo.operarios) ? infoTomo.operarios : []
  const kpisConteos = infoTomo.kpis_conteos || {}
  const kpisAjustes = infoTomo.kpis_ajustes || {}

  const getPcts = (kpi) => {
    const total = Number(kpi.total_items) || 0
    const contados = Number(kpi.items_contados) || 0
    const dif = Number(kpi.items_diferencia) || 0
    return {
      total,
      contados,
      dif,
      avance: total > 0 ? (contados / total) * 100 : 0,
      diferencia: total > 0 ? (dif / total) * 100 : 0,
    }
  }

  const dataConteos = getPcts(kpisConteos)
  const dataAjustes = getPcts(kpisAjustes)

  // Función infalible de impresión en nueva ventana (Evita problemas con React y CSS Global)
  const handlePrintRequest = async () => {
    // 1. Abrimos la pestaña antes del await para evitar el bloqueo anti-popups de los navegadores
    const printWindow = window.open("", "_blank")
    if (!printWindow) {
      showWarning("El navegador bloqueó la ventana de impresión. Por favor, permita las ventanas emergentes.")
      return
    }

    printWindow.document.write(
      '<html><body><h3 style="font-family: sans-serif; text-align: center; margin-top: 50px;">Generando reporte, por favor espere...</h3></body></html>',
    )

    setIsPrinting(true)
    setModalImprimir(false)

    try {
      const response = await api.post("/TomaFisica/getReporteDiferencias", {
        tomnumero: tomnumero_state,
        tipo_reporte: tipoReporte,
      })
      const items = response?.data?.data || []

      const origenTexto = tipoReporte === "CONTEO" ? "CONTEO MÓVIL EN VIVO" : "AJUSTE CONSOLIDADO OFICIAL"
      const dateStr = new Date().toLocaleString()

      // Construcción del HTML puro y limpio (Texto seleccionable, sin cabeceras del sistema)
      let rowsHtml = ""
      items.forEach((row) => {
        const difCls = row.diferencia !== 0 ? "color: #d32f2f; font-weight: bold;" : ""
        rowsHtml += `
          <tr>
            <td>${row.artcodigo}</td>
            <td>${row.artdescri}</td>
            <td class="num">${row.tomsaldo.toFixed(2)}</td>
            <td class="num">${row.total_contado.toFixed(2)}</td>
            <td class="num" style="${difCls}">${row.diferencia.toFixed(2)}</td>
            <td style="text-align: center;">${row.estado_dif}</td>
          </tr>
        `
      })

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Reporte de Diferencias - ${infoTomo.tomnumero}</title>
          <style>
            @page { size: A4 portrait; margin: 15mm; }
            body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: black; margin: 0; padding: 0; background: white; -webkit-print-color-adjust: exact; }
            .header-titles { text-align: center; margin-bottom: 20px; }
            .header-titles h2 { margin: 0; font-size: 20px; text-transform: uppercase; font-weight: bold; color: #333; }
            .header-titles h3 { margin: 5px 0; font-size: 14px; color: #666; font-weight: normal; }
            .header-titles h4 { margin: 15px 0 5px 0; font-size: 16px; font-weight: bold; text-decoration: underline; color: #000; }
            
            .info-grid { display: flex; justify-content: space-between; border-bottom: 2px solid black; padding-bottom: 10px; margin-bottom: 20px; font-size: 13px; }
            .info-grid > div { line-height: 1.5; }
            
            table { width: 100%; border-collapse: collapse; margin-bottom: 40px; page-break-inside: auto; }
            tr { page-break-inside: avoid; page-break-after: auto; }
            thead { display: table-header-group; }
            th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
            th { background-color: #f0f0f0; font-weight: bold; text-align: center; border-bottom: 2px solid #aaa; color: #000; }
            td.num { text-align: right; }
            
            .signatures { display: flex; justify-content: space-around; margin-top: 60px; page-break-inside: avoid; }
            .sig-box { width: 200px; text-align: center; font-size: 13px; }
            .sig-line { border-top: 1px solid black; padding-top: 5px; margin-bottom: 5px; font-weight: bold; }
            
            .footer { text-align: right; font-size: 11px; color: #666; margin-top: 30px; }
          </style>
        </head>
        <body>
          <div class="header-titles">
            <h2>${ciaNombre}</h2>
            <h3>${locDescri}</h3>
            <h4>REPORTE DE DIFERENCIAS DE INVENTARIO FÍSICO</h4>
          </div>
          
          <div class="info-grid">
            <div>
              <div><b>Tomo Nro:</b> ${infoTomo.tomnumero}</div>
              <div><b>Descripción:</b> ${infoTomo.tomdescripcion}</div>
            </div>
            <div style="text-align: right;">
              <div><b>Bodega:</b> ${infoTomo.bodcodigo}</div>
              <div><b>Origen de Datos:</b> ${origenTexto}</div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th style="width: 15%;">Código</th>
                <th style="width: 40%;">Descripción del Artículo</th>
                <th style="width: 10%;">Sistema</th>
                <th style="width: 10%;">Físico</th>
                <th style="width: 10%;">Diferencia</th>
                <th style="width: 15%;">Estado</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>

          <div class="signatures">
            <div class="sig-box">
              <br/><br/><br/>
              <div class="sig-line">Elaborado Por</div>
              <div>${usuarioLogueado}</div>
            </div>
            <div class="sig-box">
              <br/><br/><br/>
              <div class="sig-line">Revisado Por</div>
              <div>Jefe de Bodega</div>
            </div>
            <div class="sig-box">
              <br/><br/><br/>
              <div class="sig-line">Aprobado Por</div>
              <div>Gerencia / Auditoría</div>
            </div>
          </div>

          <div class="footer">
            Generado el: ${dateStr}
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                // Cierra la pestaña sola cuando el usuario termina de imprimir
                setTimeout(function() { window.close(); }, 500);
              }, 500);
            };
          </script>
        </body>
        </html>
      `

      // Sobreescribimos la ventana con el reporte final
      printWindow.document.open()
      printWindow.document.write(htmlContent)
      printWindow.document.close()
      setIsPrinting(false)
    } catch (error) {
      showWarning("Error al generar el reporte de impresión.")
      setIsPrinting(false)
      printWindow.close()
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "0 30px 30px 30px" }}
        >
          <Typography variant="h5" fontWeight="bold">
            Consulta de Tomo Físico y Rendimiento
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={() => setModalImprimir(true)}
            disabled={isLoading || dataConteos.total === 0}
          >
            Imprimir Reporte
          </Button>
        </Box>

        <CustomBackdrop isLoading={isLoading || isPrinting} />

        <Box sx={StyledRoot}>
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3, background: "white" }}>
            <Typography variant="h6" color="primary" gutterBottom sx={{ mb: 3, fontWeight: "bold" }}>
              Información del Inventario Planificado
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Número de Tomo"
                  value={infoTomo.tomnumero || ""}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Estado Actual"
                  value={getEstadoLegible(infoTomo.tomstatus)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Bodega"
                  value={infoTomo.bodcodigo || ""}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descripción General"
                  value={infoTomo.tomdescripcion || ""}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* SECCIÓN 2: KPIs EN TIEMPO REAL (CONTEOS) */}
            <Typography
              variant="h6"
              color="primary"
              gutterBottom
              sx={{ mb: 2, display: "flex", alignItems: "center", fontWeight: "bold" }}
            >
              <AnalyticsIcon sx={{ mr: 1 }} /> Rendimiento y Diferencias desde Conteos Realizados
            </Typography>
            <Grid container spacing={4} mb={2}>
              <Grid item xs={12} md={6}>
                <Box bgcolor="#e3f2fd" p={2} borderRadius={2} border="1px solid #bbdefb">
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Avance de Conteo en Vivo (<b>{dataConteos.avance.toFixed(1)}%</b>)
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={dataConteos.avance}
                    color="primary"
                    sx={{ height: 12, borderRadius: 5, mb: 1 }}
                  />
                  <Typography variant="body2" fontWeight="bold" color="primary.main">
                    Se han contado {dataConteos.contados} de {dataConteos.total} ítems planificados.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box bgcolor="#fff3e0" p={2} borderRadius={2} border="1px solid #ffcc80">
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Índice de Diferencias Proyectadas (<b>{dataConteos.diferencia.toFixed(1)}%</b>)
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={dataConteos.diferencia}
                    color="warning"
                    sx={{ height: 12, borderRadius: 5, mb: 1 }}
                  />
                  <Typography variant="body2" fontWeight="bold" color="warning.main">
                    {dataConteos.dif} ítems presentan faltantes o sobrantes.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Impacto en Dólares (Sobrantes Proyectados)"
                  value={formatCurrency(kpisConteos.valor_sobrantes)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true, style: { color: "#2E7D32", fontWeight: "bold" } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Impacto en Dólares (Faltantes Proyectados)"
                  value={formatCurrency(kpisConteos.valor_faltantes)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true, style: { color: "#d32f2f", fontWeight: "bold" } }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* SECCIÓN 3: KPIs CONSOLIDADOS (AJUSTES) */}
            <Typography
              variant="h6"
              color="secondary"
              gutterBottom
              sx={{ mb: 2, display: "flex", alignItems: "center", fontWeight: "bold" }}
            >
              <AnalyticsIcon sx={{ mr: 1 }} /> Rendimiento y Diferencias desde Ajustes Aplicados
            </Typography>
            <Grid container spacing={4} mb={2}>
              <Grid item xs={12} md={6}>
                <Box bgcolor="#f0fdf4" p={2} borderRadius={2} border="1px solid #c8e6c9">
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Conteo Consolidado Oficial (<b>{dataAjustes.avance.toFixed(1)}%</b>)
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={dataAjustes.avance}
                    color="success"
                    sx={{ height: 12, borderRadius: 5, mb: 1 }}
                  />
                  <Typography variant="body2" fontWeight="bold" color="success.main">
                    Se consolidaron {dataAjustes.contados} de {dataAjustes.total} ítems.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box bgcolor="#ffebee" p={2} borderRadius={2} border="1px solid #ffcdd2">
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Índice de Diferencias Ajustadas (<b>{dataAjustes.diferencia.toFixed(1)}%</b>)
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={dataAjustes.diferencia}
                    color="error"
                    sx={{ height: 12, borderRadius: 5, mb: 1 }}
                  />
                  <Typography variant="body2" fontWeight="bold" color="error.main">
                    {dataAjustes.dif} ítems generaron ajuste contable.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Impacto en Dólares (Sobrantes Ajustados)"
                  value={formatCurrency(kpisAjustes.valor_sobrantes)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true, style: { color: "#2E7D32", fontWeight: "bold" } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Impacto en Dólares (Faltantes Ajustados)"
                  value={formatCurrency(kpisAjustes.valor_faltantes)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true, style: { color: "#d32f2f", fontWeight: "bold" } }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* SECCIÓN 4: AVANCE POR OPERARIO */}
            <Typography
              variant="h6"
              color="primary"
              gutterBottom
              sx={{ mb: 2, display: "flex", alignItems: "center", fontWeight: "bold" }}
            >
              <AssignmentIndIcon sx={{ mr: 1 }} /> Avance de Captura Móvil por Operario
            </Typography>
            <Grid container spacing={3} mb={4}>
              {operarios.length > 0 ? (
                operarios.map((op, idx) => {
                  const pctOperario = dataConteos.total > 0 ? (Number(op.contados) / dataConteos.total) * 100 : 0
                  return (
                    <Grid item xs={12} sm={6} md={4} key={idx}>
                      <Card
                        variant="outlined"
                        sx={{ borderRadius: 2, borderColor: "#e0e0e0", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}
                      >
                        <CardContent sx={{ pb: "16px !important" }}>
                          <Box display="flex" alignItems="center" mb={1}>
                            <PersonIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="subtitle2" fontWeight="bold" color="textPrimary" noWrap>
                              {op.nombre ? `${op.codigo} - ${op.nombre}` : op.codigo}
                            </Typography>
                          </Box>
                          <Box mt={2}>
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                              <Typography variant="caption" color="textSecondary" fontWeight="bold">
                                Progreso Individual
                              </Typography>
                              <Typography variant="caption" color="primary" fontWeight="bold">
                                {pctOperario.toFixed(1)}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={pctOperario}
                              sx={{ height: 8, borderRadius: 4, bgcolor: "#e0f2f1" }}
                            />
                            <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                              Ha escaneado <b>{op.contados}</b> ítems únicos de {dataConteos.total}.
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )
                })
              ) : (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary" sx={{ p: 2, bgcolor: "#f5f7fa", borderRadius: 2 }}>
                    No hay personal asignado.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Box>

        {/* MODAL CONFIGURACIÓN IMPRESIÓN */}
        <Dialog open={modalImprimir} onClose={() => setModalImprimir(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: "#f5f5f5", color: "primary.main", fontWeight: "bold" }}>
            Configuración de Impresión
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body1" gutterBottom>
              Seleccione la versión del reporte que desea emitir:
            </Typography>
            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <RadioGroup value={tipoReporte} onChange={(e) => setTipoReporte(e.target.value)}>
                <FormControlLabel
                  value="CONTEO"
                  control={<Radio color="primary" />}
                  label="1. Rendimiento en Vivo (Conteos Móviles)"
                />
                <FormControlLabel
                  value="AJUSTE"
                  control={<Radio color="primary" />}
                  label="2. Rendimiento Oficial (Ajustes Consolidados)"
                  disabled={dataAjustes.total === 0}
                />
              </RadioGroup>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setModalImprimir(false)} color="inherit">
              Cancelar
            </Button>
            <Button variant="contained" color="primary" onClick={handlePrintRequest} startIcon={<PrintIcon />}>
              Generar y Abrir PDF
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  )
}

export default BuscarTomaFisica
